<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import CheckModal from "@/components/CheckModal";
import DocumentActions from "@/components/document/DocumentActions.vue";
import acpmInvestigationProcedureAPI from "@/services/standards/acpmInvestigation/acpmInvestigationProcedureAPI";
import newScoresAPI from "@/services/newScores/newScoresAPI";
import Swal from "sweetalert2";

const DEFAULT_OBJETIVO =
  "Establecer la metodología para garantizar la ejecución, seguimiento y verificación de la eficacia de las acciones correctivas, preventivas y de mejora derivadas de la investigación de incidentes, accidentes de trabajo y enfermedades laborales, con el fin de eliminar las causas que originaron el evento, prevenir su recurrencia y fortalecer el SG-SST.";
const DEFAULT_ALCANCE =
  "Aplica para todos los trabajadores, contratistas, aprendices, visitantes y demás personas que desarrollen actividades para la organización, con independencia de su modalidad de contratación. Comprende desde la finalización de la investigación del evento hasta el cierre definitivo de todas las acciones establecidas.";

export default {
  page: { title: "Procedimiento ACPM de Investigaciones" },
  components: { Layout, PageHeader, CheckModal, DocumentActions },
  data() {
    return {
      title: "7.1.3 Procedimiento de Ejecución de ACPM (Investigaciones)",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        { text: "7.1.3 Ejecución de ACPM de investigaciones", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      procedureId: this.$route.params.procedure || null,
      form: {
        version: "1.0",
        approval_date: new Date().toISOString().substring(0, 10),
        change_description: "Emisión inicial del procedimiento.",
        responsible: null,
        objetivo: DEFAULT_OBJETIVO,
        alcance: DEFAULT_ALCANCE,
        observacion: null,
      },
      workflowStatus: "draft",
      signedAt: null,
      submitting: false,
      loading: true,
    };
  },
  computed: {
    isSigned() {
      return this.workflowStatus === "signed";
    },
    procedureApi() {
      return {
        pdfUrl: (id) => acpmInvestigationProcedureAPI.pdfUrl(id),
        sign: (id, payload) => acpmInvestigationProcedureAPI.sign(id, payload),
      };
    },
  },
  methods: {
    goBack() { this.$router.go(-1); },
    load() {
      if (!this.procedureId) { this.loading = false; return; }
      acpmInvestigationProcedureAPI.get(this.procedureId)
        .then((r) => {
          if (!r) { this.loading = false; return; }
          this.form.version = r.version || "1.0";
          this.form.approval_date = r.approval_date ? String(r.approval_date).substring(0, 10) : this.form.approval_date;
          this.form.change_description = r.change_description || null;
          this.form.responsible = r.responsible || null;
          this.form.objetivo = r.objetivo || DEFAULT_OBJETIVO;
          this.form.alcance = r.alcance || DEFAULT_ALCANCE;
          this.form.observacion = r.observacion || null;
          this.workflowStatus = r.workflow_status || "draft";
          this.signedAt = r.signed_at || null;
          this.loading = false;
        })
        .catch((e) => { console.log(e); this.loading = false; });
    },
    save() {
      if (this.isSigned) return;
      this.submitting = true;
      const payload = { companies_id: this.companyID, ...this.form };
      const req = this.procedureId ? acpmInvestigationProcedureAPI.update(this.procedureId, payload) : acpmInvestigationProcedureAPI.save(payload);
      req
        .then((res) => {
          newScoresAPI.calculateScores({ companies_id: this.companyID, item_id: 59, status: "Pendiente" }).catch(() => {});
          Swal.fire({ title: "¡Éxito!", text: "Procedimiento guardado.", icon: "success", timer: 1600, showConfirmButton: false });
          this.submitting = false;
          if (!this.procedureId && res && res.id) {
            this.procedureId = res.id;
            this.$router.replace({ path: `/forms/estandares/${this.companyID}/acpm-inv-procedimiento/${res.id}` });
            this.load();
          }
        })
        .catch((e) => { console.log(e); this.submitting = false; Swal.fire("Error", "No se pudo guardar el procedimiento", "error"); });
    },
    onSigned(payload) {
      this.workflowStatus = payload.workflow_status;
      this.signedAt = payload.signed_at;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-9" v-if="!loading">
        <div class="card">
          <div class="card-body">
            <CheckModal>
              <p class="text-justify">
                Documento estructurado del procedimiento de ejecución de ACPM derivadas de investigaciones. El marco
                legal, las definiciones, la metodología (5 pasos), las evidencias, el indicador de gestión y los
                criterios de cierre son texto fijo del procedimiento; aquí editas la identificación de la versión y las
                secciones de objetivo y alcance. Cada guardado es una versión que puede firmarse; usa "Nueva versión" en
                el listado para conservar el historial.
              </p>
            </CheckModal>

            <div v-if="isSigned" class="alert alert-success py-2 my-2 small">
              🔒 Esta versión ya fue firmada; los datos quedan bloqueados. Crea una "Nueva versión" desde el listado para
              registrar cambios en el tiempo.
            </div>

            <form @submit.prevent="save">
              <div class="form-row">
                <b-form-group class="col-md-2 px-2" label="Versión">
                  <b-form-input type="text" v-model="form.version" :disabled="isSigned" />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Fecha de aprobación">
                  <b-form-input type="date" v-model="form.approval_date" :disabled="isSigned" required />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Responsable">
                  <b-form-input type="text" v-model="form.responsible" :disabled="isSigned" />
                </b-form-group>
                <b-form-group class="col-md-4 px-2" label="Descripción del cambio">
                  <b-form-input type="text" v-model="form.change_description" :disabled="isSigned" />
                </b-form-group>
              </div>

              <b-form-group label="1. Objetivo">
                <b-form-textarea v-model="form.objetivo" :disabled="isSigned" rows="3" max-rows="8" />
              </b-form-group>
              <b-form-group label="2. Alcance">
                <b-form-textarea v-model="form.alcance" :disabled="isSigned" rows="3" max-rows="8" />
              </b-form-group>
              <b-form-group label="Observaciones (opcional)">
                <b-form-input type="text" v-model="form.observacion" :disabled="isSigned" />
              </b-form-group>

              <p class="text-muted small">
                Secciones fijas incluidas automáticamente en el PDF: marco legal, definiciones, responsabilidades,
                desarrollo, metodología (5 pasos), evidencias, indicador de gestión, criterios de cierre y anexos.
              </p>

              <div class="text-right">
                <button type="submit" :disabled="isSigned || submitting" class="btn btn-primary mx-1">Guardar</button>
                <button type="button" class="btn btn-secondary mx-1" @click="goBack">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="col-lg-3" v-if="!loading && procedureId">
        <h6 class="text-muted small mb-1">Procedimiento ACPM (Investigaciones)</h6>
        <DocumentActions
          :document-id="Number(procedureId)"
          :company-id="companyID"
          :workflow-status="workflowStatus"
          :signed-at="signedAt"
          :api-service="procedureApi"
          document-title="Procedimiento de Ejecución de ACPM derivadas de investigaciones"
          signer-role="gerente"
          @signed="onSigned"
        />
      </div>

      <div v-if="loading" class="col-12 text-center"><b-spinner variant="primary" label="Loading..."></b-spinner></div>
    </div>
  </Layout>
</template>
